/**
 * SignIn — /signin
 *
 * Multi-option sign-in hub. Every option except "Continue with
 * SocialPulse" works standalone in this fork; the SocialPulse button
 * preflights the hub's OIDC discovery endpoint and shows a friendly
 * "coming soon" state when the hub isn't reachable (avoids the
 * 404-detail=Not-Found landing page reported by users).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  LogIn, Mail, Phone, MessageCircle, Loader2, ChevronRight, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getSocialPulseSsoConfig, startSocialPulseLogin,
} from "@/lib/socialpulseOidc";

const OIDC_TIMEOUT_MS = 4000;

export default function SignIn() {
  const nav       = useNavigate();
  const [params]  = useSearchParams();
  const returnTo  = params.get("next") || "/";
  const { user, sendOtp, verifyOtp, sendEmailLink, signInWithGoogleProvider } = useAuth();

  const cfg = useMemo(() => getSocialPulseSsoConfig(), []);
  const [oidcStatus, setOidcStatus] = useState("checking"); // checking | ready | unavailable

  // Redirect signed-in users straight to their destination.
  useEffect(() => {
    if (user) nav(returnTo, { replace: true });
  }, [user, nav, returnTo]);

  // Same-origin preflight — hit our backend probe which server-side
  // fetches the hub's discovery doc. This avoids the CORS wall that
  // blocks a direct browser fetch to the hub's .well-known endpoint.
  useEffect(() => {
    const ctl  = new AbortController();
    const kill = setTimeout(() => ctl.abort(), OIDC_TIMEOUT_MS);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/socialpulse/status`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : { configured: false, reachable: false })
      .then((s) => setOidcStatus(s.configured && s.reachable ? "ready" : "unavailable"))
      .catch(() => setOidcStatus("unavailable"))
      .finally(() => clearTimeout(kill));
    return () => { clearTimeout(kill); ctl.abort(); };
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="signin-page">
      <Link to="/" className="text-xs uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold inline-flex items-center gap-1" data-testid="signin-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>

      <div className="mt-4 flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl bg-blue-600 text-white inline-flex items-center justify-center shadow-sm">
          <MessageCircle className="w-4 h-4" />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-900">Sign in to ChatsPulse</h1>
          <p className="text-xs text-slate-500">One conversation, every community. Pick any sign-in.</p>
        </div>
      </div>

      {/* SocialPulse SSO */}
      <section className="mt-6" data-testid="signin-socialpulse-section">
        {(oidcStatus === "ready" || oidcStatus === "checking") && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/socialpulse/init?return_to=${encodeURIComponent(returnTo)}`}
            data-testid="signin-socialpulse-btn"
            className="w-full inline-flex items-center justify-between px-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            <span className="inline-flex items-center gap-2">
              {oidcStatus === "checking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Continue with SocialPulse
            </span>
            <ChevronRight className="w-4 h-4" />
          </a>
        )}
        {oidcStatus === "unavailable" && (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="signin-socialpulse-unavailable"
          >
            <div className="font-bold flex items-center gap-1.5">
              <LogIn className="w-3.5 h-3.5" /> Continue with SocialPulse — coming soon
            </div>
            <p className="mt-1 text-amber-800">
              The SocialPulse identity hub is still being provisioned. Use one of the options below to start participating today — we'll link your account automatically when SSO comes online.
            </p>
          </div>
        )}
      </section>

      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <span className="flex-1 h-px bg-slate-200" />
        <span className="uppercase tracking-widest">Or continue with</span>
        <span className="flex-1 h-px bg-slate-200" />
      </div>

      <GoogleOption signInWithGoogleProvider={signInWithGoogleProvider} nav={nav} returnTo={returnTo} />
      <EmailLinkOption sendEmailLink={sendEmailLink} />
      <PhoneOtpOption sendOtp={sendOtp} verifyOtp={verifyOtp} nav={nav} returnTo={returnTo} />

      <p className="mt-6 text-[11px] text-slate-400 text-center">
        By signing in you accept the community guidelines. Your identity is portable across the SocialPulse network.
      </p>
    </div>
  );
}

// ─── Google (Firebase) ───────────────────────────────────────
function GoogleOption({ signInWithGoogleProvider, nav, returnTo }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await signInWithGoogleProvider(true);
      toast.success("Signed in!");
      nav(returnTo, { replace: true });
    } catch (e) {
      toast.error(e.message || "Google sign-in failed.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="w-full inline-flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-sm font-semibold text-slate-800 disabled:opacity-50"
      data-testid="signin-google-btn"
    >
      <span className="inline-flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
        Continue with Google
      </span>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.5-4.8 11.5-11.6 0-.8-.1-1.4-.2-2H12z"/>
    </svg>
  );
}

// ─── Email magic link ───────────────────────────────────────
function EmailLinkOption({ sendEmailLink }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy]   = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await sendEmailLink(email.trim());
      toast.success("Check your email for a sign-in link.");
    } catch (err) {
      toast.error(err.message || "Failed to send link.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid="signin-email-section">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-sm font-semibold text-slate-800"
          data-testid="signin-email-open"
        >
          <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Email me a sign-in link</span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      ) : (
        <form onSubmit={send} className="rounded-xl border border-slate-200 p-3 bg-white space-y-2">
          <label className="text-xs font-bold text-slate-700">Your email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400"
            data-testid="signin-email-input"
            required
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
              data-testid="signin-email-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Send link
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setEmail(""); }}
              className="text-xs text-slate-500 hover:text-slate-800 px-3"
              data-testid="signin-email-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Phone OTP ──────────────────────────────────────────────
function PhoneOtpOption({ sendOtp, verifyOtp, nav, returnTo }) {
  const [open, setOpen]   = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode]   = useState("");
  const [stage, setStage] = useState("phone");    // phone | otp
  const [busy, setBusy]   = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!phone.trim().match(/^\+\d{7,15}$/)) {
      toast.error("Use full international format, e.g. +14155551234");
      return;
    }
    setBusy(true);
    try {
      await sendOtp(phone.trim());
      setStage("otp");
    } catch (err) {
      toast.error(err.message || "Couldn't send code.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) return;
    setBusy(true);
    try {
      await verifyOtp(code.trim(), true);
      toast.success("Signed in!");
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(err.message || "Code didn't work.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="signin-phone-section">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-sm font-semibold text-slate-800"
          data-testid="signin-phone-open"
        >
          <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Continue with phone number</span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      ) : stage === "phone" ? (
        <form onSubmit={send} className="rounded-xl border border-slate-200 p-3 bg-white space-y-2">
          <label className="text-xs font-bold text-slate-700">Phone number</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155551234"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400"
            data-testid="signin-phone-input"
            required
          />
          <div id="recaptcha-container" />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
              data-testid="signin-phone-send"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
              Send code
            </button>
            <button type="button" onClick={() => { setOpen(false); setPhone(""); }} className="text-xs text-slate-500 hover:text-slate-800 px-3" data-testid="signin-phone-cancel">Cancel</button>
          </div>
        </form>
      ) : (
        <form onSubmit={verify} className="rounded-xl border border-slate-200 p-3 bg-white space-y-2">
          <label className="text-xs font-bold text-slate-700">Verification code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="6-digit code"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 tracking-widest"
            data-testid="signin-phone-code"
            required minLength={4} maxLength={8}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
              data-testid="signin-phone-verify"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
              Verify & sign in
            </button>
            <button
              type="button"
              onClick={() => { setStage("phone"); setCode(""); }}
              className="text-xs text-slate-500 hover:text-slate-800 px-3"
              data-testid="signin-phone-restart"
            >
              Change number
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
